import React, { useState } from 'react';
import { Zap, Sparkles, Info, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { motorMetaForIndicador, impactoEstrategico, CLUSTERS_NOMBRES } from '../lib/motor-indicadores-data';

/**
 * Mini-banner contextual de impacto estratégico para un indicador.
 *
 * Lenguaje probabilístico/pedagógico (NO determinista).
 * Se muestra solo cuando el indicador es Tractor o de Alto impacto estratégico.
 * Para indicadores Medio / Bajo, devuelve null (no satura la interfaz).
 *
 * Props:
 *   - indicadorId: id largo del indicador (ej. 'indicador-3-1', 'indicador-m2-9')
 *   - variante: 'card' (default) | 'inline-compact'
 *   - showLow: si true, también muestra los niveles Medio/Bajo (uso interno)
 *   - className
 */
export default function IndicadorImpactBanner({ indicadorId, variante = 'card', showLow = false, className }) {
    const [open, setOpen] = useState(false);
    const meta = motorMetaForIndicador(indicadorId);
    const nivel = impactoEstrategico(meta);

    if (!meta || !nivel) return null;
    if (!showLow && (nivel === 'medio' || nivel === 'bajo')) return null;

    const NIVEL_META = {
        tractor: {
            label: 'Tractor estratégico',
            etiqueta: 'TRACTOR',
            icon: Zap,
            tono: 'border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-white text-emerald-900',
            chip: 'bg-emerald-600 text-white',
            iconBg: 'bg-emerald-100 text-emerald-700',
            mensaje: 'Indicador tractor detectado por el Motor Estratégico.',
            detalle: 'Fortalecer este indicador podría favorecer indirectamente el desempeño en otros indicadores del Portafolio. Estas relaciones se basan en correlaciones observadas en perfiles nacionales.',
        },
        alto: {
            label: 'Alto impacto estratégico',
            etiqueta: 'ALTO IMPACTO',
            icon: Sparkles,
            tono: 'border-sky-200 bg-gradient-to-br from-sky-50/80 to-white text-sky-900',
            chip: 'bg-sky-600 text-white',
            iconBg: 'bg-sky-100 text-sky-700',
            mensaje: 'Indicador de alto impacto estratégico.',
            detalle: 'Este indicador presenta alta relación con otros indicadores del Portafolio y suele aportar al resultado global. Es un buen candidato para invertir tiempo de calidad.',
        },
        medio: {
            label: 'Impacto medio',
            etiqueta: 'IMPACTO MEDIO',
            icon: Info,
            tono: 'border-slate-200 bg-slate-50/70 text-slate-700',
            chip: 'bg-slate-500 text-white',
            iconBg: 'bg-slate-100 text-slate-600',
            mensaje: 'Impacto estratégico intermedio.',
            detalle: 'Indicador con relaciones moderadas con otros del Portafolio. Apunta a Competente sólido sin sobreinvertir.',
        },
        bajo: {
            label: 'Impacto bajo',
            etiqueta: 'IMPACTO BAJO',
            icon: Info,
            tono: 'border-slate-200 bg-slate-50/40 text-slate-600',
            chip: 'bg-slate-400 text-white',
            iconBg: 'bg-slate-100 text-slate-500',
            mensaje: 'Indicador con bajo arrastre estratégico.',
            detalle: 'Asegura un buen nivel sin invertir tiempo en exceso: el retorno marginal es bajo.',
        },
    };

    const data = NIVEL_META[nivel];
    const Icon = data.icon;

    if (variante === 'inline-compact') {
        return (
            <span
                className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider',
                    data.chip,
                    className
                )}
                title={data.mensaje}
                data-testid={`impact-banner-compact-${nivel}`}
            >
                <Icon className="h-3 w-3" />
                {data.etiqueta}
            </span>
        );
    }

    return (
        <div
            className={cn(
                'rounded-xl border overflow-hidden transition-all duration-200',
                data.tono,
                className
            )}
            data-testid={`impact-banner-${nivel}`}
            data-impact-level={nivel}
            data-indicador={meta.id}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-start gap-3 px-4 py-3 text-left"
                data-testid="impact-banner-toggle"
            >
                <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', data.iconBg)}>
                    <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider', data.chip)}>
                            {data.etiqueta}
                        </span>
                        <span className="text-xs font-semibold opacity-80">según el Motor Estratégico del Portafolio</span>
                    </div>
                    <p className="mt-1.5 text-sm font-medium leading-snug">{data.mensaje}</p>
                </div>
                {open ? <ChevronDown className="mt-1 h-4 w-4 opacity-60" /> : <ChevronRight className="mt-1 h-4 w-4 opacity-60" />}
            </button>

            {open && (
                <div className="border-t border-current/10 bg-white/60 px-4 py-3 space-y-3">
                    <p className="text-xs leading-relaxed text-slate-700">
                        {data.detalle}
                    </p>

                    {CLUSTERS_NOMBRES[meta.cluster] && (
                        <p className="text-[11px] text-slate-600">
                            Cluster pedagógico: <strong className="text-slate-800">{CLUSTERS_NOMBRES[meta.cluster]}</strong>
                        </p>
                    )}

                    {nivel === 'tractor' && meta.arrastra && meta.arrastra.length > 0 && (
                        <div className="rounded-lg bg-white/80 border border-emerald-100 p-3">
                            <p className="text-[11px] font-semibold text-emerald-900 mb-1.5 flex items-center gap-1.5">
                                <ArrowRight className="h-3 w-3" />
                                Relaciones probabilísticas observadas con:
                            </p>
                            <ul className="space-y-1">
                                {meta.arrastra.map((nombre, i) => (
                                    <li key={i} className="flex items-start gap-1.5 text-[11px] text-slate-700">
                                        <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-emerald-500" />
                                        <span>{nombre}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-2 text-[10px] italic text-slate-500 leading-relaxed">
                                Estas relaciones se basan en correlaciones de desempeño a nivel nacional, no en causalidad directa.
                            </p>
                        </div>
                    )}

                    <p className="text-[10px] italic text-slate-500 leading-relaxed">
                        Lectura pedagógica, no determinista: el Motor sugiere prioridades estratégicas, las decisiones son tuyas.
                    </p>
                </div>
            )}
        </div>
    );
}
